import * as THREE from 'three';

export function initThreeScene(canvas) {
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, canvas.clientWidth / canvas.clientHeight, 0.1, 2000);
    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setSize(canvas.clientWidth, canvas.clientHeight);
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;

    // ---------- Starfield ----------
    const starCount = 2500;
    const starGeo = new THREE.BufferGeometry();
    const starPos = new Float32Array(starCount * 3);
    const starSizes = new Float32Array(starCount);
    const starColors = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount; i++) {
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(2 * Math.random() - 1);
        const dist = 300 + Math.random() * 700;
        starPos[i * 3] = Math.sin(phi) * Math.cos(theta) * dist;
        starPos[i * 3 + 1] = Math.sin(phi) * Math.sin(theta) * dist;
        starPos[i * 3 + 2] = Math.cos(phi) * dist;
        starSizes[i] = 0.5 + Math.random() * 1.5;
        const temp = Math.random();
        if (temp < 0.3) {
            starColors[i * 3] = 1; starColors[i * 3 + 1] = 0.85; starColors[i * 3 + 2] = 0.6;
        } else if (temp < 0.6) {
            starColors[i * 3] = 0.7; starColors[i * 3 + 1] = 0.8; starColors[i * 3 + 2] = 1;
        } else {
            starColors[i * 3] = 1; starColors[i * 3 + 1] = 1; starColors[i * 3 + 2] = 1;
        }
    }
    starGeo.setAttribute('position', new THREE.BufferAttribute(starPos, 3));
    starGeo.setAttribute('size', new THREE.BufferAttribute(starSizes, 1));
    starGeo.setAttribute('color', new THREE.BufferAttribute(starColors, 3));
    const starMat = new THREE.PointsMaterial({
        size: 0.8, vertexColors: true, transparent: true, opacity: 0.9,
        blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true,
    });
    const stars = new THREE.Points(starGeo, starMat);
    scene.add(stars);

    const loader = new THREE.TextureLoader();
    // ---------- Orbiting Discord Icons ----------
    const botTex = loader.load('/resources/bg,f8f8f8-flat,750x,075,f-pad,750x1000,f8f8f8.jpg');
    const orbiters = [];
    const orbiterConfigs = [
        { radius: 1.0, speed: 0.35, offset: 0, tilt: 0.3, size: 0.35 },
        { radius: 1.4, speed: -0.28, offset: 1.5, tilt: -0.2, size: 0.32 },
        { radius: 1.8, speed: 0.22, offset: 3, tilt: 0.25, size: 0.35 },
        { radius: 2.2, speed: -0.18, offset: 4.5, tilt: -0.15, size: 0.33 },
    ];

    orbiterConfigs.forEach(cfg => {
        const mat = new THREE.SpriteMaterial({ map: botTex, transparent: true, opacity: 0.75, depthTest: false });
        const sprite = new THREE.Sprite(mat);
        sprite.scale.set(cfg.size, cfg.size, 1);
        sprite.userData = cfg;
        scene.add(sprite);
        orbiters.push(sprite);

        // Glow around each
        const glowMat = new THREE.SpriteMaterial({
            map: botTex, transparent: true, opacity: 0.04, depthTest: false, depthWrite: false,
        });
        const glowSprite = new THREE.Sprite(glowMat);
        glowSprite.scale.set(cfg.size * 1.5, cfg.size * 1.5, 1);
        glowSprite.userData = { isGlow: true, parent: sprite };
        scene.add(glowSprite);
        orbiters.push(glowSprite);

        // Trail
        const trailLen = 20;
        const trailPos = [];
        for (let i = 0; i < trailLen; i++) trailPos.push(new THREE.Vector3(0, 0, 0));
        const trailGeo = new THREE.BufferGeometry();
        const trailArr = new Float32Array(trailLen * 3);
        trailGeo.setAttribute('position', new THREE.BufferAttribute(trailArr, 3));
        const trail = new THREE.Line(trailGeo, new THREE.LineBasicMaterial({
            color: 0x818cf8, transparent: true, opacity: 0.15,
        }));
        trail.userData = { trailPos, trailLen, idx: 0, parent: sprite };
        scene.add(trail);
        orbiters.push(trail);
    });

    // ---------- Data Rings (orbit paths) ----------
    const orbitData = [
        { radius: 1.0, color: 0x818cf8 },
        { radius: 1.4, color: 0x34d399 },
        { radius: 1.8, color: 0xf472b6 },
        { radius: 2.2, color: 0x818cf8 },
    ];

    orbitData.forEach(od => {
        const pts = [];
        for (let i = 0; i <= 80; i++) {
            const a = (i / 80) * Math.PI * 2;
            pts.push(new THREE.Vector3(
                Math.cos(a) * od.radius,
                0,
                Math.sin(a) * od.radius
            ));
        }
        const geo = new THREE.BufferGeometry().setFromPoints(pts);
        const line = new THREE.Line(geo, new THREE.LineBasicMaterial({
            color: od.color, transparent: true, opacity: 0.06,
        }));
        scene.add(line);
    });

    // ---------- Connection Beams ----------
    const beamPairs = [[0,1],[1,2],[2,3]];
    beamPairs.forEach(([i, j]) => {
        const curve = new THREE.QuadraticBezierCurve3(
            new THREE.Vector3(orbitData[i].radius, 0, 0),
            new THREE.Vector3((orbitData[i].radius + orbitData[j].radius) * 0.5, 0.3 + Math.random() * 0.4, 0),
            new THREE.Vector3(orbitData[j].radius, 0, 0)
        );
        const pts = curve.getPoints(24);
        const geo = new THREE.BufferGeometry().setFromPoints(pts);
        const line = new THREE.Line(geo, new THREE.LineBasicMaterial({
            color: 0x818cf8, transparent: true, opacity: 0.03,
        }));
        scene.add(line);
    });

    // ---------- Data Packets ----------
    const packetCount = 150;
    const packetGeo = new THREE.BufferGeometry();
    const packetPos = new Float32Array(packetCount * 3);
    for (let i = 0; i < packetCount; i++) {
        const od = orbitData[Math.floor(Math.random() * orbitData.length)];
        const a = Math.random() * Math.PI * 2;
        const r = od.radius + (Math.random() - 0.5) * 0.2;
        packetPos[i * 3] = Math.cos(a) * r;
        packetPos[i * 3 + 1] = (Math.random() - 0.5) * 0.1;
        packetPos[i * 3 + 2] = Math.sin(a) * r;
    }
    packetGeo.setAttribute('position', new THREE.BufferAttribute(packetPos, 3));
    const packetSpeeds = new Float32Array(packetCount);
    const packetOrbitIdx = new Uint8Array(packetCount);
    const packetAngles = new Float32Array(packetCount);
    for (let i = 0; i < packetCount; i++) {
        packetSpeeds[i] = 0.3 + Math.random() * 0.5;
        packetOrbitIdx[i] = Math.floor(Math.random() * orbitData.length);
        packetAngles[i] = Math.random() * Math.PI * 2;
    }
    const packets = new THREE.Points(packetGeo, new THREE.PointsMaterial({
        size: 0.025, color: 0x818cf8, transparent: true, opacity: 0.4,
        blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true,
    }));
    scene.add(packets);

    // ---------- Flow Particles ----------
    const flowCount = 100;
    const flowGeo = new THREE.BufferGeometry();
    const flowPos = new Float32Array(flowCount * 3);
    for (let i = 0; i < flowCount; i++) {
        const a = Math.random() * Math.PI * 2;
        const r = 0.6 + Math.random() * 1.2;
        flowPos[i * 3] = Math.cos(a) * r;
        flowPos[i * 3 + 1] = (Math.random() - 0.5) * 0.8;
        flowPos[i * 3 + 2] = Math.sin(a) * r;
    }
    flowGeo.setAttribute('position', new THREE.BufferAttribute(flowPos, 3));
    const flowParticles = new THREE.Points(flowGeo, new THREE.PointsMaterial({
        size: 0.02, color: 0x60a5fa, transparent: true, opacity: 0.2,
        blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true,
    }));
    scene.add(flowParticles);

    // ---------- Nebula haze ----------
    const nebCount = 500;
    const nebGeo = new THREE.BufferGeometry();
    const nebPos = new Float32Array(nebCount * 3);
    const nebCols = new Float32Array(nebCount * 3);
    for (let i = 0; i < nebCount; i++) {
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(2 * Math.random() - 1);
        const d = 40 + Math.random() * 120;
        nebPos[i * 3] = Math.sin(phi) * Math.cos(theta) * d;
        nebPos[i * 3 + 1] = Math.sin(phi) * Math.sin(theta) * d * 0.3;
        nebPos[i * 3 + 2] = Math.cos(phi) * d;
        const c = new THREE.Color().setHSL(0.72 + Math.random() * 0.15, 0.5, 0.03 + Math.random() * 0.04);
        nebCols[i * 3] = c.r; nebCols[i * 3 + 1] = c.g; nebCols[i * 3 + 2] = c.b;
    }
    nebGeo.setAttribute('position', new THREE.BufferAttribute(nebPos, 3));
    nebGeo.setAttribute('color', new THREE.BufferAttribute(nebCols, 3));
    const nebula = new THREE.Points(nebGeo, new THREE.PointsMaterial({
        size: 1.5, vertexColors: true, transparent: true, opacity: 0.12,
        blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true,
    }));
    scene.add(nebula);

    // Camera
    camera.position.set(0, 4.5, 0.01);
    camera.lookAt(0, 0, 0);

    let mouseX = 0, mouseY = 0;
    let time = 0;
    let prevW = canvas.clientWidth, prevH = canvas.clientHeight;

    canvas.addEventListener('mousemove', e => {
        mouseX = (e.clientX / canvas.clientWidth) * 2 - 1;
        mouseY = -(e.clientY / canvas.clientHeight) * 2 + 1;
    });

    function resize() {
        const w = canvas.clientWidth, h = canvas.clientHeight;
        if (w !== prevW || h !== prevH) {
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
            renderer.setSize(w, h);
            prevW = w; prevH = h;
        }
    }

    const raf = { id: null };

    function animate() {
        raf.id = requestAnimationFrame(animate);
        resize();
        time += 0.005;

        // Starfield rotation
        stars.rotation.y += 0.00005;

        // Orbiters
        for (let i = 0; i < orbiters.length; i++) {
            const obj = orbiters[i];
            if (obj.userData.isGlow) {
                const parent = obj.userData.parent;
                obj.position.copy(parent.position);
                continue;
            }
            if (obj.userData.trailLen) {
                const parent = obj.userData.parent;
                const pos = obj.userData;
                pos.trailPos[pos.idx] = parent.position.clone();
                pos.idx = (pos.idx + 1) % pos.trailLen;
                const arr = obj.geometry.attributes.position.array;
                for (let j = 0; j < pos.trailLen; j++) {
                    const t = pos.trailPos[(pos.idx + j) % pos.trailLen];
                    if (t) { arr[j * 3] = t.x; arr[j * 3 + 1] = t.y; arr[j * 3 + 2] = t.z; }
                }
                obj.geometry.attributes.position.needsUpdate = true;
                continue;
            }
            const cfg = obj.userData;
            const angle = time * cfg.speed + cfg.offset;
            const x = Math.cos(angle) * cfg.radius;
            const z = Math.sin(angle) * cfg.radius;
            const y = Math.sin(angle * 2) * cfg.tilt;
            obj.position.set(x, y, z);
        }

        // Data packets
        const pPos = packets.geometry.attributes.position.array;
        for (let i = 0; i < packetCount; i++) {
            packetAngles[i] += 0.003 * packetSpeeds[i];
            const od = orbitData[packetOrbitIdx[i]];
            const a = packetAngles[i];
            const r = od.radius + (Math.sin(i + time) * 0.15);
            pPos[i * 3] = Math.cos(a) * r;
            pPos[i * 3 + 1] = 0;
            pPos[i * 3 + 2] = Math.sin(a) * r;
        }
        packets.geometry.attributes.position.needsUpdate = true;

        // Flow particles drift
        const fPos = flowParticles.geometry.attributes.position.array;
        for (let i = 0; i < flowCount; i++) {
            fPos[i * 3 + 1] += Math.sin(time * 0.5 + i) * 0.001;
            const a = Math.atan2(fPos[i * 3 + 2], fPos[i * 3]);
            const r = Math.sqrt(fPos[i * 3] ** 2 + fPos[i * 3 + 2] ** 2);
            const newA = a + 0.001;
            fPos[i * 3] = Math.cos(newA) * r;
            fPos[i * 3 + 2] = Math.sin(newA) * r;
        }
        flowParticles.geometry.attributes.position.needsUpdate = true;

        // Camera mouse parallax
        const tx = mouseX * 0.6;
        const ty = mouseY * 0.4;
        camera.position.x += (tx - camera.position.x) * 0.02;
        camera.position.z += (ty - (camera.position.z - 0.01)) * 0.02;
        camera.lookAt(0, 0, 0);

        renderer.render(scene, camera);
    }

    animate();

    return {
        dispose() {
            cancelAnimationFrame(raf.id);
            renderer.dispose();
            scene.traverse(obj => {
                if (obj.geometry) obj.geometry.dispose();
                if (obj.material) obj.material.dispose();
            });
        }
    };
}
